import { useEffect, useState } from "react";
import type { IWasteMovementDwtReadiness } from "@/customTypings/wasteMovement/WasteMovement";
import { wasteMovementsService } from "@/services/wasteMovements/wasteMovements.service";
import { logErrorForDev } from "@/utils";

export type WasteMovementDwtReadinessState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "loaded"; readiness: IWasteMovementDwtReadiness };

/**
 * S2A: the server's DWT readiness for a saved Waste Received movement. The server is the gate; this is advisory display.
 * `savedVersion` (e.g. the record's `updatedAtUtc`) re-checks after each save, since readiness reflects the saved record.
 */
export function useWasteMovementDwtReadiness(
    siteId: string | undefined,
    movementId: string | undefined,
    savedVersion: string | undefined,
    enabled: boolean
): WasteMovementDwtReadinessState | null {
    const [state, setState] = useState<WasteMovementDwtReadinessState | null>(null);

    useEffect(() => {
        if (!enabled || !siteId || !movementId) {
            setState(null);
            return;
        }
        let cancelled = false;
        setState({ status: "loading" });
        wasteMovementsService
            .getDwtReadiness(siteId, movementId)
            .then((readiness) => {
                if (!cancelled) setState({ status: "loaded", readiness });
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtReadiness", e);
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [siteId, movementId, savedVersion, enabled]);

    return state;
}
